<template>
  <div :class="isDialog ? 'preview-dialog-wrapper' : 'preview-page-wrapper'">
    <!-- Background Ambient Light Orbs -->
    <div v-if="!isDialog" class="bg-orb bg-orb-1"></div>
    <div v-if="!isDialog" class="bg-orb bg-orb-2"></div>

    <!-- Fixed Header for Dialog Mode -->
    <div
      v-if="isDialog"
      class="preview-dialog-top-bar d-flex justify-space-between align-center px-6 py-4 no-print"
    >
      <v-chip color="primary" outlined>
        <v-avatar left color="transparent">
          <v-icon color="primary">mdi-check-circle</v-icon>
        </v-avatar>
        Order Preview
      </v-chip>
      <v-btn
        @click="closePreview"
        class="action-btn back-btn white--text"
        rounded
        elevation="3"
      >
        <v-icon left>mdi-close</v-icon>
        Close Preview
      </v-btn>
    </div>

    <div :class="isDialog ? 'dialog-scroll-content' : ''">
      <v-container class="py-6 px-4 max-width-container">
        <!-- TOP ACTION BAR for Page Mode -->
        <div
          v-if="!isDialog"
          class="d-flex justify-end align-center mb-6 flex-wrap no-print"
        >
          <v-btn
            @click="closePreview"
            class="action-btn back-btn white--text"
            rounded
            elevation="3"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back To Report
          </v-btn>
        </div>

        <!-- Main Preview Card -->
        <v-card
          class="preview-page-card pa-6 bg-preview-light font-outfit"
          flat
        >
          <!-- MASSIVE HERO HEADER -->
          <v-card
            class="hero-banner mb-6 position-relative overflow-hidden"
            flat
          >
            <div class="hero-glow-overlay"></div>

            <!-- Floating Decorative Background Icons -->
            <v-icon
              class="bg-deco-icon icon-1"
              color="rgba(255, 255, 255, 0.05)"
              >mdi-credit-card-outline</v-icon
            >
            <v-icon
              class="bg-deco-icon icon-2"
              color="rgba(255, 255, 255, 0.05)"
              >mdi-wallet-outline</v-icon
            >
            <v-icon
              class="bg-deco-icon icon-3"
              color="rgba(255, 255, 255, 0.05)"
              >mdi-security</v-icon
            >

            <!-- Ocean Wave Effect at bottom -->
            <div class="hero-waves">
              <svg
                class="waves-svg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 24 150 28"
                preserveAspectRatio="none"
              >
                <defs>
                  <path
                    id="wave-path"
                    d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
                  />
                </defs>
                <g class="parallax-waves">
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="0"
                    fill="rgba(255, 255, 255, 0.05)"
                  />
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="3"
                    fill="rgba(255, 255, 255, 0.08)"
                  />
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="5"
                    fill="rgba(255, 255, 255, 0.03)"
                  />
                  <use
                    xlink:href="#wave-path"
                    x="48"
                    y="7"
                    fill="rgba(255, 255, 255, 0.12)"
                  />
                </g>
              </svg>
            </div>

            <v-row
              class="ma-0 align-center position-relative fill-height text-content"
            >
              <!-- Left side Info -->
              <v-col cols="12" md="8" class="pa-8 z-index-2">
                <div class="d-flex align-center">
                  <v-avatar
                    color="rgba(255, 255, 255, 0.15)"
                    size="56"
                    class="mr-4 glow-icon-box"
                  >
                    <v-icon color="white" large>mdi-receipt</v-icon>
                  </v-avatar>
                  <div>
                    <h1 class="hero-title white--text">
                      JCSAT Purchase Details
                    </h1>
                    <p class="hero-subtitle mb-0">
                      View complete order, payment and delivery information.
                    </p>
                  </div>
                </div>
              </v-col>

              <!-- Right side Status Card -->
              <v-col
                cols="12"
                md="4"
                class="pa-8 text-md-right d-flex justify-md-end z-index-2"
              >
                <div
                  class="status-badge-card pa-4 rounded-xl d-inline-flex align-center"
                >
                  <div class="mr-3 text-left">
                    <span class="status-label">Order Status</span>
                    <span class="status-value font-weight-black d-block"
                      >PAID</span
                    >
                  </div>
                  <v-avatar
                    color="rgba(76, 175, 80, 0.15)"
                    size="48"
                    class="pulse-avatar-green"
                  >
                    <v-icon color="#4caf50">mdi-check-bold</v-icon>
                  </v-avatar>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- ORDER SUMMARY STRIP -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Order ID</span>
                    <h3 class="strip-val font-mono">
                      #{{
                        order.RzrpayPaymentId
                          ? order.RzrpayPaymentId.substring(4, 12)
                          : "JCSAT" + order.MobileNo.substring(6)
                      }}
                    </h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-tag-outline</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Order Date</span>
                    <h3 class="strip-val">{{ order.OrdersDateTxt }}</h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-calendar-month-outline</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Total Kits</span>
                    <h3 class="strip-val font-weight-black">
                      {{ order.OrderQty }} Units
                    </h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-package-variant-closed</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" lg="3" class="py-2">
              <v-card class="strip-card glass-card pa-4 rounded-xl">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <span class="strip-label">Total Amount</span>
                    <h3
                      class="strip-val price-val text-gradient-blue font-weight-black"
                    >
                      ₹{{
                        parseFloat(order.ReceivedAmount).toLocaleString("en-IN")
                      }}
                    </h3>
                  </div>
                  <v-avatar color="rgba(21, 101, 192, 0.08)" size="44">
                    <v-icon color="#1565C0">mdi-currency-inr</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- TWO COLUMNS GRID: SECTION 1 & 2 -->
          <v-row class="mb-6">
            <!-- MEMBER INFORMATION -->
            <v-col cols="12" md="6" class="py-2">
              <v-card
                class="section-card glass-card pa-6 rounded-2xl fill-height"
              >
                <div class="d-flex align-center mb-6">
                  <v-avatar
                    color="rgba(21, 101, 192, 0.1)"
                    size="48"
                    class="mr-3"
                  >
                    <v-icon color="#1565C0">mdi-account-outline</v-icon>
                  </v-avatar>
                  <h2 class="section-title">Member Information</h2>
                </div>

                <div class="d-flex align-center flex-wrap mb-6">
                  <v-avatar
                    size="64"
                    color="#1565C0"
                    class="member-large-avatar mr-4"
                  >
                    <span class="white--text font-weight-bold headline">{{
                      order.MemberFullname
                        ? order.MemberFullname.charAt(0).toUpperCase()
                        : "M"
                    }}</span>
                  </v-avatar>
                  <div>
                    <h3 class="member-title-name">
                      {{ order.MemberFullname }}
                    </h3>
                    <span class="member-subtitle-id"
                      >ID: {{ order.MembershipId }}</span
                    >
                  </div>
                </div>

                <v-divider class="mb-6 opacity-08"></v-divider>

                <v-row>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">Zone</span>
                    <span class="item-val">{{ order.ZoneName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">LOM</span>
                    <span class="item-val">{{ order.LomName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">Email Address</span>
                    <span class="item-val break-word">{{ order.EmailId }}</span>
                  </v-col>
                  <v-col cols="6" class="py-2">
                    <span class="item-lbl">Mobile Number</span>
                    <span class="item-val">{{ order.MobileNo }}</span>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <!-- DELIVERY INFORMATION -->
            <v-col cols="12" md="6" class="py-2">
              <v-card
                class="section-card glass-card pa-6 rounded-2xl fill-height"
              >
                <div class="d-flex align-center mb-6">
                  <v-avatar
                    color="rgba(21, 101, 192, 0.1)"
                    size="48"
                    class="mr-3"
                  >
                    <v-icon color="#1565C0">mdi-truck-delivery</v-icon>
                  </v-avatar>
                  <h2 class="section-title">Delivery Information</h2>
                </div>

                <div
                  class="d-flex align-center justify-space-between mb-4 flex-wrap"
                >
                  <span
                    class="shipping-badge px-3 py-1.5 rounded-pill font-weight-bold"
                  >
                    📦 Shipping Address Verified
                  </span>
                  <div class="map-pulse-container mr-4">
                    <v-icon color="#1565C0" class="map-marker-pulse"
                      >mdi-map-marker</v-icon
                    >
                  </div>
                </div>

                <v-divider class="mb-6 opacity-08"></v-divider>

                <v-row>
                  <v-col cols="12" class="py-2">
                    <span class="item-lbl">Delivery Contact Name</span>
                    <span class="item-val font-weight-bold">{{
                      order.MemberFullname
                    }}</span>
                  </v-col>
                  <v-col cols="12" class="py-2">
                    <span class="item-lbl">Delivery Mobile Number</span>
                    <span class="item-val">{{ order.MobileNo }}</span>
                  </v-col>
                  <v-col cols="12" class="py-2">
                    <span class="item-lbl">Delivery Address</span>
                    <span class="item-val address-txt">
                      LOM Registered Address, c/o {{ order.LomName }},
                      {{ order.ZoneName }}, India
                    </span>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <!-- PURCHASED KITS SECTION -->
          <v-card class="section-card glass-card pa-6 rounded-2xl mb-6">
            <div class="d-flex align-center mb-6">
              <v-avatar color="rgba(21, 101, 192, 0.1)" size="48" class="mr-3">
                <v-icon color="#1565C0">mdi-book-open</v-icon>
              </v-avatar>
              <h2 class="section-title">Purchased Kits</h2>
            </div>

            <v-row>
              <v-col
                v-for="(kit, index) in parsedKits"
                :key="'kit-' + index"
                cols="12"
                sm="6"
                md="4"
                class="py-3"
              >
                <v-card
                  class="kit-product-card pa-4 rounded-xl relative-content fill-height"
                >
                  <div class="premium-ribbon font-weight-bold">JCSAT PLAN</div>
                  <div class="d-flex align-center mb-4 mt-2">
                    <v-avatar
                      color="rgba(66, 165, 245, 0.1)"
                      size="50"
                      class="mr-3"
                    >
                      <v-icon color="#42A5F5" size="28"
                        >mdi-book-multiple</v-icon
                      >
                    </v-avatar>
                    <div>
                      <h4 class="kit-title">{{ kit.name }}</h4>
                      <span class="kit-sub-qty"
                        >{{ kit.qty }} Units Ordered</span
                      >
                    </div>
                  </div>

                  <v-divider class="mb-4 opacity-08"></v-divider>

                  <div class="d-flex align-center justify-space-between mt-2">
                    <span class="kit-card-lbl">Estimated Price</span>
                    <span
                      class="kit-card-val font-weight-bold text-gradient-blue"
                    >
                      ₹{{
                        (
                          parseFloat(order.ReceivedAmount) / parsedKits.length
                        ).toLocaleString("en-IN", { maximumFractionDigits: 0 })
                      }}
                    </span>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card>

          <!-- SECTION 4, 5 & 6 (TIMELINE & FINTECH PAYMENT DETS) -->
          <v-row class="mb-6">
            <!-- PAYMENT TIMELINE -->
            <v-col cols="12" md="4" class="py-2">
              <v-card
                class="section-card glass-card pa-6 rounded-2xl fill-height"
              >
                <div class="d-flex align-center mb-6">
                  <v-avatar
                    color="rgba(21, 101, 192, 0.1)"
                    size="48"
                    class="mr-3"
                  >
                    <v-icon color="#1565C0">mdi-clock</v-icon>
                  </v-avatar>
                  <h2 class="section-title">Payment Timeline</h2>
                </div>

                <div class="timeline-container pl-6">
                  <div class="timeline-step completed">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                      <h4 class="timeline-step-title">Order Created</h4>
                      <p class="timeline-step-desc">
                        Order request registered in system.
                      </p>
                    </div>
                  </div>

                  <div class="timeline-step completed">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                      <h4 class="timeline-step-title">Payment Initiated</h4>
                      <p class="timeline-step-desc">
                        Redirected to secure payment gateway.
                      </p>
                    </div>
                  </div>

                  <div class="timeline-step completed">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                      <h4 class="timeline-step-title">Payment Successful</h4>
                      <p class="timeline-step-desc">
                        Gateway response authorized successfully.
                      </p>
                    </div>
                  </div>

                  <div class="timeline-step completed">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                      <h4 class="timeline-step-title">Order Confirmed</h4>
                      <p class="timeline-step-desc">
                        Materials booked and approved for distribution.
                      </p>
                    </div>
                  </div>

                  <div class="timeline-step active">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                      <h4 class="timeline-step-title">Ready For Dispatch</h4>
                      <p class="timeline-step-desc">
                        Package queued for shipment and logistics.
                      </p>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>

            <!-- PAYMENT INFORMATION (FINTECH CARD) -->
            <v-col cols="12" md="4" class="py-2">
              <v-card
                class="section-card glass-card pa-6 rounded-2xl fill-height fintech-card"
              >
                <div class="d-flex align-center mb-6">
                  <v-avatar
                    color="rgba(21, 101, 192, 0.1)"
                    size="48"
                    class="mr-3"
                  >
                    <v-icon color="#1565C0">mdi-shield-check-outline</v-icon>
                  </v-avatar>
                  <h2 class="section-title">Payment Information</h2>
                </div>

                <div class="razorpay-feel-box pa-4 rounded-xl mb-4">
                  <div class="d-flex align-center justify-space-between mb-4">
                    <span class="rp-label">Razorpay Payment ID</span>
                    <code class="rp-tag">{{
                      order.RzrpayPaymentId || "N/A"
                    }}</code>
                  </div>
                  <div class="d-flex align-center justify-space-between mb-4">
                    <span class="rp-label">Payment Method</span>
                    <span class="rp-value">Netbanking / Cards</span>
                  </div>
                  <div class="d-flex align-center justify-space-between mb-4">
                    <span class="rp-label">Status</span>
                    <span class="rp-value-success">SUCCESS</span>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="rp-label">Transaction Date</span>
                    <span class="rp-value">{{ order.OrdersDateTxt }}</span>
                  </div>
                </div>

                <v-divider class="mb-4 opacity-08"></v-divider>

                <div class="d-flex justify-space-between py-1">
                  <span class="text-caption text-muted">Base Amount</span>
                  <span class="text-caption font-weight-bold"
                    >₹{{ getCalculatedSubtotal }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-1">
                  <span class="text-caption text-muted">GST Tax (18%)</span>
                  <span class="text-caption font-weight-bold"
                    >₹{{ getCalculatedGst }}</span
                  >
                </div>
              </v-card>
            </v-col>

            <!-- PURCHASE SUMMARY (RECEIPT CARD) -->
            <v-col cols="12" md="4" class="py-2">
              <v-card
                class="section-card glass-card pa-6 rounded-2xl fill-height receipt-summary-card d-flex flex-column justify-space-between"
              >
                <div>
                  <div class="d-flex align-center mb-6">
                    <v-avatar
                      color="rgba(21, 101, 192, 0.1)"
                      size="48"
                      class="mr-3"
                    >
                      <v-icon color="#1565C0">mdi-receipt</v-icon>
                    </v-avatar>
                    <h2 class="section-title">Purchase Summary</h2>
                  </div>

                  <div class="summary-breakdown">
                    <div class="d-flex justify-space-between py-2">
                      <span class="summary-lbl">Subtotal</span>
                      <span class="summary-val"
                        >₹{{ getCalculatedSubtotal }}</span
                      >
                    </div>
                    <div class="d-flex justify-space-between py-2">
                      <span class="summary-lbl">GST (18%)</span>
                      <span class="summary-val">₹{{ getCalculatedGst }}</span>
                    </div>
                    <v-divider class="my-3"></v-divider>
                    <div class="d-flex justify-space-between py-2 align-center">
                      <span class="summary-lbl font-weight-bold headline"
                        >Grand Total</span
                      >
                      <span
                        class="summary-val grand-price text-gradient-blue font-weight-black"
                      >
                        ₹{{
                          parseFloat(order.ReceivedAmount).toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                            }
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  class="receipt-success-footer mt-6 pa-4 rounded-xl d-flex align-center"
                >
                  <v-icon color="#2e7d32" class="mr-3"
                    >mdi-check-circle-outline</v-icon
                  >
                  <div>
                    <span class="success-footer-title d-block"
                      >Payment Complete</span
                    >
                    <span class="success-footer-subtitle"
                      >Authorized by Razorpay Safe Gateway</span
                    >
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
    </div>
  </div>
</template>

<script>
export default {
  name: "JcsatOrderPreview",
  props: {
    orderData: {
      type: Object,
      default: null,
    },
    isDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      order: null,
    };
  },
  watch: {
    orderData: {
      immediate: true,
      handler(val) {
        if (val) {
          this.order = val;
        }
      },
    },
  },
  computed: {
    parsedKits() {
      if (!this.order || !this.order.KitDetails) return [];
      return this.order.KitDetails.split(",").map((item) => {
        const trimmed = item.trim();
        const match =
          trimmed.match(/(.+?)\s*\((\d+)\s*(?:Qty|Units)?\)$/i) ||
          trimmed.match(/(.+?)\s*-\s*(\d+)$/);
        if (match) {
          return {
            name: match[1].trim(),
            qty: match[2],
          };
        }
        return { name: trimmed, qty: "0" };
      });
    },
    getCalculatedSubtotal() {
      if (!this.order) return "0.00";
      const base = parseFloat(this.order.ReceivedAmount) / 1.18;
      return base.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getCalculatedGst() {
      if (!this.order) return "0.00";
      const total = parseFloat(this.order.ReceivedAmount);
      const base = total / 1.18;
      const gst = total - base;
      return gst.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  created() {
    if (this.orderData) {
      this.order = this.orderData;
    } else {
      const stored = localStorage.getItem("selected_jcsat_order");
      if (stored) {
        try {
          this.order = JSON.parse(stored);
        } catch (e) {
          console.error("Error parsing stored order:", e);
        }
      }
      if (!this.order && !this.isDialog) {
        this.$router.push("/reports/jcsat-kit-purchase-report");
      }
    }
  },
  methods: {
    closePreview() {
      if (this.isDialog) {
        this.$emit("close");
      } else {
        this.$router.push("/reports/jcsat-kit-purchase-report");
      }
    },
    printReceipt() {
      window.print();
    },
    getParticleStyle(index) {
      const topOffset = [15, 60, 25, 75, 40, 85];
      const leftOffset = [10, 20, 45, 65, 80, 90];
      const scale = [1, 1.5, 0.8, 1.2, 1.6, 0.9];
      const speed = [8, 12, 6, 10, 14, 7];
      return {
        position: "absolute",
        top: `${topOffset[index - 1]}%`,
        left: `${leftOffset[index - 1]}%`,
        transform: `scale(${scale[index - 1]})`,
        animation: `floatParticle ${
          speed[index - 1]
        }s infinite ease-in-out alternate`,
      };
    },
  },
};
</script>

<style scoped>
/*
PRESET
  STYLES
  FOR
  THE
  PREMIUM
  PREVIEW
  DIALOG
  ==========================================================================
  */
.preview-dialog-container {
  border-radius: 24px !important;
  overflow: hidden;
}

.preview-dialog-wrapper {
  background-color: #f6f8fc !important;
  border-radius: 24px !important;
  overflow: hidden;
  position: relative;
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif !important;
}

.preview-dialog-wrapper
  *:not(.rp-tag):not(.font-mono):not(code):not(v-icon):not(.v-icon) {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif !important;
}

.preview-dialog-top-bar {
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.top-bar-title {
  font-size: 16px;
  color: #263238;
}

.close-dialog-btn {
  color: #78909c !important;
}

.dialog-scroll-content {
  max-height: 80vh;
  overflow-y: auto;
}

.bg-preview-light {
  background-color: #f6f8fc;
}

/* Hero card customization */
.hero-banner {
  background: linear-gradient(
    135deg,
    #1565c0 0%,
    #1a73e8 45%,
    #42a5f5 100%
  ) !important;
  border-radius: 24px !important;
  color: white;
  min-height: 250px;
  box-shadow: 0 16px 40px rgba(21, 101, 192, 0.25) !important;
  animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.hero-glow-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.05),
    rgba(0, 0, 0, 0.15)
  );
  z-index: 1;
}

.hero-glow-overlay::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 7s infinite ease-in-out;
}

.text-content {
  position: relative;
  z-index: 3;
}

.hero-title {
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.8px;
}

.hero-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
}

/* Background floating icons */
.bg-deco-icon {
  position: absolute !important;
  z-index: 1;
}

.icon-1 {
  font-size: 90px !important;
  right: 15%;
  top: -10px;
  transform: rotate(15deg);
  animation: floatShape 10s infinite ease-in-out;
}

.icon-2 {
  font-size: 70px !important;
  left: 10%;
  bottom: 10px;
  transform: rotate(-10deg);
  animation: floatShape 12s infinite ease-in-out alternate;
}

.icon-3 {
  font-size: 80px !important;
  right: 40%;
  bottom: -15px;
  transform: rotate(25deg);
  animation: floatShape 14s infinite ease-in-out;
}

/* Parallax Ocean Waves */
.hero-waves {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  z-index: 2;
  pointer-events: none;
}

.waves-svg {
  width: 100%;
  height: 40px;
}

.parallax-waves > use {
  animation: waveFlow 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax-waves > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax-waves > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax-waves > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax-waves > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

/* Action Buttons */
.action-btn {
  font-weight: 700 !important;
  font-size: 13px !important;
  padding: 10px 24px !important;
  text-transform: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.back-btn {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
}

.print-btn {
  background: linear-gradient(135deg, #455a64 0%, #607d8b 100%) !important;
}

.download-btn {
  background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%) !important;
}

.action-btn:hover {
  transform: translateY(-2.5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
}

/* Status Card */
.status-badge-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.status-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.7);
}

.status-value {
  font-size: 22px;
  color: white;
}

/* Glassmorphism Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  box-shadow: 0 10px 30px rgba(21, 101, 192, 0.04) !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.glass-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 16px 36px rgba(21, 101, 192, 0.08) !important;
  border-color: rgba(21, 101, 192, 0.15) !important;
}

/* Strip cards */
.strip-card {
  border-left: 4px solid #1565c0 !important;
  animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.strip-label {
  font-size: 11px;
  color: #78909c;
  font-weight: 600;
  text-transform: uppercase;
  display: block;
}

.strip-val {
  font-size: 16px;
  font-weight: 700;
  color: #263238;
}

.price-val {
  font-size: 20px;
}

/* Section Cards */
.section-title {
  font-size: 18px;
  font-weight: 800;
  color: #1565c0;
}

.member-large-avatar {
  border: 3.5px solid rgba(21, 101, 192, 0.2);
  box-shadow: 0 4px 15px rgba(21, 101, 192, 0.15);
}

.member-title-name {
  font-size: 18px;
  font-weight: 700;
  color: #263238;
}

.member-subtitle-id {
  font-size: 12px;
  color: #78909c;
  font-weight: 500;
}

.item-lbl {
  font-size: 11px;
  color: #78909c;
  font-weight: 600;
  display: block;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.item-val {
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
}

.break-word {
  word-break: break-all;
}

.address-txt {
  line-height: 1.5;
}

/* Shipping Verified badge */
.shipping-badge {
  background: rgba(76, 175, 80, 0.08);
  border: 1px solid rgba(76, 175, 80, 0.15);
  color: #2e7d32;
  font-size: 12px;
}

/* Map Pulse Marker */
.map-pulse-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.map-marker-pulse {
  font-size: 28px !important;
  animation: pulseGreen 2.5s infinite ease-in-out;
}

/* Purchased Kits - Ecommerce Cards */
.kit-product-card {
  background: white !important;
  border: 1px solid rgba(21, 101, 192, 0.08) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.03) !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
  position: relative;
  overflow: hidden;
}

.kit-product-card:hover {
  transform: translateY(-6px) scale(1.02);
  border-color: rgba(21, 101, 192, 0.25) !important;
  box-shadow: 0 16px 36px rgba(21, 101, 192, 0.12) !important;
}

.premium-ribbon {
  position: absolute;
  top: 15px;
  right: -30px;
  background: linear-gradient(135deg, #1565c0 0%, #42a5f5 100%);
  color: white;
  padding: 4px 30px;
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transform: rotate(45deg);
}

.kit-title {
  font-size: 14px;
  font-weight: 700;
  color: #263238;
}

.kit-sub-qty {
  font-size: 12px;
  color: #78909c;
  font-weight: 600;
}

.kit-card-lbl {
  font-size: 11px;
  color: #90a4ae;
  font-weight: 600;
  text-transform: uppercase;
}

.kit-card-val {
  font-size: 16px;
}

/* Fintech card & Razorpay styling */
.fintech-card {
  border-top: 5px solid #1565c0 !important;
}

.razorpay-feel-box {
  background: rgba(21, 101, 192, 0.03);
  border: 1.5px dashed rgba(21, 101, 192, 0.15);
}

.rp-label {
  font-size: 11px;
  color: #546e7a;
  font-weight: 600;
}

.rp-tag {
  font-family: "Courier New", Courier, monospace;
  font-weight: 700;
  color: #1565c0;
  background: rgba(21, 101, 192, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
}

.rp-value {
  font-size: 12px;
  font-weight: 600;
  color: #263238;
}

.rp-value-success {
  font-size: 11px;
  font-weight: 700;
  color: #2e7d32;
  background: rgba(76, 175, 80, 0.12);
  padding: 2px 8px;
  border-radius: 4px;
}

/* Timeline details */
.timeline-container {
  position: relative;
  border-left: 2px solid rgba(21, 101, 192, 0.15);
}

.timeline-step {
  position: relative;
  padding-bottom: 24px;
}

.timeline-step:last-child {
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -27px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #cfd8dc;
  border: 2px solid white;
  z-index: 2;
  transition: all 0.3s ease;
}

.timeline-step.completed .timeline-dot {
  background-color: #2e7d32;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
}

.timeline-step.active .timeline-dot {
  background-color: #1565c0;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.25);
}

.timeline-step-title {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  margin-bottom: 2px;
}

.timeline-step-desc {
  font-size: 11px;
  color: #78909c;
  margin: 0;
}

/* Receipt Summary Card */
.summary-lbl {
  font-size: 13px;
  color: #546e7a;
  font-weight: 600;
}

.summary-val {
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
}

.grand-price {
  font-size: 22px;
}

.receipt-success-footer {
  background: rgba(76, 175, 80, 0.08);
  border: 1px solid rgba(76, 175, 80, 0.15);
}

.success-footer-title {
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
}

.success-footer-subtitle {
  font-size: 11px;
  color: #546e7a;
}

/* Particle styling */
.particle-effect {
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  pointer-events: none;
  z-index: 1;
}

/* Gradient text helpers */
.text-gradient-blue {
  background: linear-gradient(135deg, #1565c0 0%, #42a5f5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Animations */
@keyframes shineSweep {
  0% {
    left: -120%;
  }
  15%,
  100% {
    left: 120%;
  }
}

@keyframes waveFlow {
  0% {
    transform: translate3d(-90px, 0, 0);
  }
  100% {
    transform: translate3d(85px, 0, 0);
  }
}

@keyframes floatShape {
  0%,
  100% {
    transform: translateY(0) rotate(15deg) scale(1);
  }
  50% {
    transform: translateY(-15px) rotate(20deg) scale(1.05);
  }
}

@keyframes floatParticle {
  0% {
    transform: translateY(0px) scale(1);
  }
  100% {
    transform: translateY(-20px) scale(1.2);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseGreen {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(76, 175, 80, 0));
  }
  50% {
    transform: scale(1.1);
    filter: drop-shadow(0 0 8px rgba(76, 175, 80, 0.6));
  }
}

.pulse-avatar-green {
  animation: pulseGreen 2.5s infinite ease-in-out;
}

/* Z-Index helpers */
.z-index-2 {
  position: relative;
  z-index: 2;
}

/* Print View Formatting */
@media print {
  .no-print {
    display: none !important;
  }
  .preview-dialog-wrapper {
    background-color: white !important;
    padding: 0 !important;
  }
  .bg-orb {
    display: none !important;
  }
  .hero-banner {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
    background: #1565c0 !important;
    color: white !important;
    min-height: auto !important;
  }
  .glass-card {
    background: white !important;
    border: 1px solid #ccc !important;
    box-shadow: none !important;
    transform: none !important;
  }
  .strip-card {
    border-left: 4px solid #1565c0 !important;
  }
}
</style>
